import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';

const VoiceSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [scriptsVisible, setScriptsVisible] = useState([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
          [0, 1, 2, 3, 4].forEach((i) => setTimeout(() => setScriptsVisible(prev => [...prev, i]), i * 120));
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const scripts = [
    "I am calm. I am present. I am grounded in this moment.",
    "I am safe in my body and trust the wisdom within me.",
    "I am worthy of love, peace, and everything I desire.",
    "I am the author of my thoughts. I choose what I repeat.",
    "I am becoming the version of me I have always known I could be."
  ];

  return (
    <section id="voice" ref={sectionRef} className="relative py-20 px-6 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="/image1.jpg"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover opacity-[0.13] scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-transparent to-background/60" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto">
        <div className="md:grid md:grid-cols-2 md:gap-16 md:items-center">
          {/* Left: Explanation */}
          <div className={`mb-12 md:mb-0 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500/20 to-purple-500/20 flex items-center justify-center mb-6">
              <Icon name="Mic" size={28} className="text-violet-400" />
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
              Hear it in{' '}
              <span className="text-gradient">your own voice.</span>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              DepthMe speaks your daily guidance back to you in your own voice — so new thoughts feel familiar, believable, natural.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              Because the brain trusts the voice it knows best: yours.
            </p>
            <div className="glassmorphism p-4 rounded-xl border border-primary/20">
              <p className="text-sm text-muted-foreground">
                <span className="text-accent font-medium">How it works:</span> During onboarding, you read 5 short affirmations aloud — about 45–90 seconds. DepthMe clones your voice and uses it to deliver all your personalized guidance.
              </p>
              <p className="text-xs text-muted-foreground mt-2 opacity-70">
                Your voice is yours. You can delete it anytime in Settings.
              </p>
            </div>
          </div>

          {/* Right: Scripts */}
          <div>
            <h3 className={`text-lg font-semibold text-foreground mb-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              Read these out loud during setup
            </h3>
            <div className="space-y-3">
              {scripts.map((script, index) => (
                <div
                  key={index}
                  className={`glassmorphism p-4 rounded-2xl flex items-start space-x-3 transition-all duration-700 ${
                    scriptsVisible.includes(index) ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-primary">{index + 1}</span>
                  </div>
                  <p className="text-muted-foreground italic leading-relaxed text-sm">
                    "{script}"
                  </p>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-4 opacity-60">
              Speak each line slowly. Pause briefly between them.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VoiceSection;
